import React, { useState, useRef, useEffect } from 'react';
import { Steps, Form, Input, Select, Button, Upload, message, Card, Row, Col } from 'antd';
import { InboxOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import type { UploadFile } from 'antd/es/upload/interface';
import { useNavigate } from 'react-router-dom';
import { faultReportService } from '../../services/api/fault-report.service';
import LocationPicker from './LocationPicker';

const { Step } = Steps;
const { TextArea } = Input;
const { Dragger } = Upload;
const { Option } = Select;

const faultTypes = [
  { value: 'leak', label: 'Curenje vode' },
  { value: 'meter_damage', label: 'Oštećenje vodomjera' },
  { value: 'low_pressure', label: 'Slab pritisak vode' },
  { value: 'quality', label: 'Problem sa kvalitetom vode' },
  { value: 'connection', label: 'Problem sa priključkom' },
  { value: 'other', label: 'Ostalo' }
];

const FaultReportForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user, form]);

  const handleNext = async () => {
    try {
      await form.validateFields(getFieldsForCurrentStep());
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const getFieldsForCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return ['name', 'email', 'phone', 'fault_type', 'description'];
      case 1:
        return ['address', 'city', 'latitude', 'longitude', 'location_details', 'images'];
      default:
        return [];
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      console.log('Form values:', values); // Debug log
      
      const formData = new FormData();
      
      // Basic validation and proper type checking before appending
      if (typeof values.name === 'string' && values.name) {
        formData.append('name', values.name);
      }
      
      if (typeof values.email === 'string' && values.email) {
        formData.append('email', values.email);
      }
      
      if (typeof values.phone === 'string' && values.phone) {
        formData.append('phone', values.phone);
      }
      
      if (typeof values.fault_type === 'string' && values.fault_type) {
        formData.append('fault_type', values.fault_type);
      }
      
      if (typeof values.description === 'string' && values.description) {
        formData.append('description', values.description);
      }
      
      if (typeof values.address === 'string' && values.address) {
        formData.append('address', values.address);
      }
      
      if (typeof values.city === 'string' && values.city) {
        formData.append('city', values.city);
      }

      if (values.latitude && values.longitude) {
        formData.append('latitude', values.latitude.toString());
        formData.append('longitude', values.longitude.toString());
      }
      
      if (typeof values.location_details === 'string' && values.location_details) {
        formData.append('location_details', values.location_details);
      }

      // Handle images properly
      if (fileList && fileList.length > 0) {
        fileList.forEach(file => {
          if (file.originFileObj instanceof File) {
            formData.append('images[]', file.originFileObj);
          }
        });
      }
      
      // Debug log to see what's being sent
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      await faultReportService.create(formData);
      
      message.success('Prijava kvara je uspješno poslata');
      navigate('/issues');
    } catch (error) {
      message.error('Greška pri slanju prijave');
    } finally {
      setSubmitting(false);
    }
  };

  const uploadProps = {
    beforeUpload: (file: UploadFile) => {
      if (fileList.length >= 3) {
        message.error('Možete dodati maksimalno 3 slike');
        return false;
      }
      setFileList([...fileList, file]);
      return false;
    },
    onRemove: (file: UploadFile) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    fileList,
    multiple: true,
    accept: 'image/*',
    maxCount: 3,
    listType: 'picture-card' as const,
  };

  const steps = [
    {
      title: 'Osnovne informacije',
      content: (
        <>
          <Form.Item
            name="name"
            label="Ime i prezime"
            rules={[
              { required: true, message: 'Unesite ime i prezime' },
              { min: 2, message: 'Ime mora imati najmanje 2 karaktera' },
              { whitespace: true, message: 'Ime ne može biti prazan prostor' }
            ]}
          >
            <Input placeholder="Unesite vaše ime i prezime" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: 'email', message: 'Unesite ispravan email' },
              { required: !form.getFieldValue('phone'), message: 'Unesite email ili broj telefona' },
              { whitespace: true, message: 'Email ne može biti prazan prostor' }
            ]}
            dependencies={['phone']}
          >
            <Input placeholder="Unesite vašu email adresu" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Broj telefona"
            rules={[
              { required: !form.getFieldValue('email'), message: 'Unesite broj telefona ili email' },
              { pattern: /^[0-9+\-\s()]*$/, message: 'Unesite ispravan format telefona' },
              { min: 6, message: 'Broj telefona mora imati najmanje 6 brojeva' }
            ]}
            dependencies={['email']}
          >
            <Input placeholder="Unesite vaš broj telefona (npr. 066123456)" />
          </Form.Item>

          <Form.Item
            name="fault_type"
            label="Tip prijave"
            rules={[{ required: true, message: 'Izaberite tip prijave' }]}
          >
            <Select placeholder="Izaberite tip prijave">
              {faultTypes.map(type => (
                <Option key={type.value} value={type.value}>{type.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Opis problema"
            rules={[{ required: true, message: 'Unesite opis problema' }]}
          >
            <TextArea rows={4} placeholder="Detaljno opišite problem" />
          </Form.Item>
        </>
      ),
    },
    {
      title: 'Lokacija i slike',
      content: (
        <>
          {/* Hidden fields for location data */}
          <Form.Item name="latitude" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="longitude" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="city" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="address"
            label="Lokacija kvara"
            rules={[{ required: true, message: 'Molimo odaberite lokaciju kvara' }]}
          >
            <LocationPicker 
              onChange={(location) => {
                form.setFieldsValue({
                  address: location.address,
                  city: location.city,
                  latitude: location.latitude,
                  longitude: location.longitude
                });
              }}
            />
          </Form.Item>

          <Form.Item
            name="location_details"
            label="Dodatne informacije o lokaciji"
          >
            <TextArea rows={2} placeholder="Npr. prvi sprat, stan 4, ulaz iz dvorišta..." />
          </Form.Item>

          <Form.Item
            name="images"
            label="Slike (min 1, max 3)"
            rules={[{ 
              required: true, 
              validator: () => {
                if (fileList.length === 0) {
                  return Promise.reject('Dodajte najmanje jednu sliku');
                }
                return Promise.resolve();
              }
            }]}
          >
            <Upload {...uploadProps}>
              {fileList.length < 3 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Dodaj sliku</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </>
      ),
    },
  ];

  return (
    <Card title="Nova prijava kvara" className="max-w-4xl mx-auto">
      <Steps current={currentStep} className="mb-8">
        {steps.map(step => (
          <Step key={step.title} title={step.title} />
        ))}
      </Steps>

      <Form
        form={form}
        layout="vertical"
        className="max-w-2xl mx-auto"
      >
        {steps[currentStep].content}

        <div className="flex justify-between mt-8">
          {currentStep > 0 && (
            <Button onClick={handlePrevious}>
              Nazad
            </Button>
          )}
          {currentStep < steps.length - 1 ? (
            <Button type="primary" onClick={handleNext}>
              Dalje
            </Button>
          ) : (
            <Button type="primary" onClick={handleSubmit} loading={submitting}>
              Pošalji prijavu
            </Button>
          )}
        </div>
      </Form>
    </Card>
  );
};

export default FaultReportForm;